import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./TimelineInterference.css";

/**
 * TimelineInterference — experimental "future-signal breaking through"
 * micro-glitch that plays across `/` once every ~80–100 seconds.
 *
 * Rendered through a React PORTAL into `document.body` so no parent
 * transform / stacking context / overflow-hidden can clip the overlay.
 * That guarantees true full-viewport coverage on every route and every
 * scroll position.
 *
 * To disable the experiment: set `ENABLE_TIMELINE_INTERFERENCE = false`
 * OR remove the `<TimelineInterference />` mount line from ProphetsHome.
 */

// -------------------------------------------------------------
// Feature flag — flip to false to disable the experiment.
// -------------------------------------------------------------
const ENABLE_TIMELINE_INTERFERENCE = false;

// -------------------------------------------------------------
// Timing constants — the whole choreography lives here so both
// the JS phases and the CSS keyframes stay in sync.
// -------------------------------------------------------------
const EFFECT_TOTAL_MS = 2000;    // total on-screen duration of one event
const PHASE1_MS       = 350;     // digital burn-in (site collapses)
const PHASE2_MS       = 300;     // signal materialization (text glitches in)
const PHASE3_MS       = 750;     // stable transmission (message readable)
const PHASE4_MS       = 600;     // signal breakup + return

const SIGNAL_START_MS = PHASE1_MS;                       // text mounts at 350 ms
const SIGNAL_END_MS   = PHASE1_MS + PHASE2_MS + PHASE3_MS + Math.min(200, PHASE4_MS); // fades out with breakup

const MIN_INTERVAL_MS = 80_000;
const MAX_INTERVAL_MS = 100_000;
const POSTPONE_MS     = 8_000;

const SIGNAL_MESSAGES = [
  "TIMELINE SHIFT DETECTED",
  "FUTURE SIGNAL RECEIVED",
  "PROPHECY FRAGMENT 07",
  "OUTCOME RECALCULATED",
  "TEMPORAL ANOMALY",
  "ALTERNATE FUTURE IDENTIFIED",
  "SIGNAL SOURCE UNKNOWN",
  "PREDICTION STREAM INTERRUPTED",
  "TIMELINE BRANCH ACTIVE",
  "FUTURE STATE UNSTABLE",
];
const pickMessage = () => SIGNAL_MESSAGES[Math.floor(Math.random() * SIGNAL_MESSAGES.length)];
const nextInterval = () => MIN_INTERVAL_MS + Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS);

function isUserBusy() {
  if (typeof document === "undefined") return true;
  if (document.hidden) return true;
  if (typeof document.hasFocus === "function" && !document.hasFocus()) return true;
  if (document.querySelector('[data-testid="prophecy-modal"]')) return true;
  if (document.querySelector('[role="dialog"][aria-modal="true"]')) return true;
  const wheel = document.querySelector(".prophecy-wheel");
  if (wheel && (wheel.classList.contains("is-spinning") || wheel.classList.contains("is-revealing"))) return true;
  const el = document.activeElement;
  if (el) {
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    if (el.isContentEditable) return true;
    if (el.getAttribute && el.getAttribute("aria-expanded") === "true") return true;
  }
  return false;
}

// Six horizontal burn strips — used for the collapse animation. Each strip
// gets a slightly different animation-delay so the collapse looks fragmented.
const BURN_STRIPS = 6;

export default function TimelineInterference() {
  const [active, setActive] = useState(false);
  const [message, setMessage] = useState(SIGNAL_MESSAGES[0]);
  const [showSignal, setShowSignal] = useState(false);
  const [breaking, setBreaking] = useState(false);
  const timers = useRef([]);
  const runningRef = useRef(false);

  useEffect(() => {
    if (!ENABLE_TIMELINE_INTERFERENCE) return undefined;
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
    if (mq && mq.matches) return undefined;

    const clearAll = () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current = [];
    };
    const setTimer = (fn, ms) => {
      const id = window.setTimeout(fn, ms);
      timers.current.push(id);
      return id;
    };

    const trigger = () => {
      if (runningRef.current) { setTimer(trigger, POSTPONE_MS); return; }
      if (isUserBusy())        { setTimer(trigger, POSTPONE_MS); return; }
      runningRef.current = true;
      setMessage(pickMessage());
      setBreaking(false);
      setShowSignal(false);
      setActive(true);

      // Mount the signal at the start of phase 2 (right after burn-in).
      setTimer(() => setShowSignal(true), SIGNAL_START_MS);
      // Trigger the phase-4 breakup animation.
      setTimer(() => setBreaking(true), PHASE1_MS + PHASE2_MS + PHASE3_MS);
      // Unmount signal shortly into phase 4 (fragments fade during breakup).
      setTimer(() => setShowSignal(false), SIGNAL_END_MS);
      // End of event — restore normal state and schedule the next event.
      setTimer(() => {
        setActive(false);
        setBreaking(false);
        runningRef.current = false;
        setTimer(trigger, nextInterval());
      }, EFFECT_TOTAL_MS);
    };

    setTimer(trigger, nextInterval());
    return clearAll;
  }, []);

  if (!ENABLE_TIMELINE_INTERFERENCE) return null;
  if (!active) return null;
  if (typeof document === "undefined") return null;

  // Portal to <body> so no parent stacking context / transform / overflow
  // rule can clip or shrink the overlay.
  return createPortal(
    <div
      className={`ti-root${breaking ? " is-breaking" : ""}`}
      data-testid="timeline-interference"
      aria-hidden="true"
      role="presentation"
    >
      {/* Phase 1 — six horizontal "burn" strips collapse into full black */}
      <div className="ti-burn" aria-hidden="true">
        {Array.from({ length: BURN_STRIPS }).map((_, i) => (
          <span
            key={i}
            className="ti-burn-strip"
            style={{
              top: `${(i / BURN_STRIPS) * 100}%`,
              height: `${100 / BURN_STRIPS}%`,
              animationDelay: `${i * 30}ms`,
            }}
          />
        ))}
      </div>

      {/* Persistent full-black layer that holds through phases 2–3 */}
      <span className="ti-black" />

      {/* Signal message with glitch materialization + breakup */}
      {showSignal && (
        <div className={`ti-signal-wrap${breaking ? " is-breaking" : ""}`}>
          <span className="ti-signal-bracket ti-signal-bracket--l" />
          <div className="ti-signal" data-text={message}>
            <span className="ti-signal-layer ti-signal-layer--base">{message}</span>
            <span className="ti-signal-layer ti-signal-layer--cyan">{message}</span>
            <span className="ti-signal-layer ti-signal-layer--violet">{message}</span>
          </div>
          <span className="ti-signal-bracket ti-signal-bracket--r" />
          <span className="ti-signal-scan" />
        </div>
      )}
    </div>,
    document.body,
  );
}
